import { createSlice } from '@reduxjs/toolkit';

/**
 * Query slice to manage queries state across the application.
 * This allows components to access queries for determining edit access
 * and showing query indicators on fields.
 */
const initialState = {
  // All queries for the current subject/form context
  queries: [],
  // Queries indexed by fieldKey for quick lookup
  queriesByFieldKey: {},
  // Loading state
  isLoading: false,
  // Error state
  error: null,
  // Context information for the current queries
  context: {
    subjectMasterId: null,
    crfMasterId: null,
    stepKey: null,
    sectionKey: null,
    subSectionKey: null,
  },
  // Counter to trigger refetch when queries are updated
  refetchTrigger: 0,
};

const querySlice = createSlice({
  name: 'queries',
  initialState,
  reducers: {
    /**
     * Set queries and index them by fieldKey
     */
    setQueries: (state, action) => {
      const queries = action.payload || [];
      state.queries = queries;
      state.isLoading = false;
      state.error = null;

      // Index queries by fieldKey for quick lookup
      state.queriesByFieldKey = {};
      queries.forEach((query) => {
        if (query.fieldKey) {
          if (!state.queriesByFieldKey[query.fieldKey]) {
            state.queriesByFieldKey[query.fieldKey] = [];
          }
          state.queriesByFieldKey[query.fieldKey].push(query);
        }
      });
    },

    /**
     * Set loading state
     */
    setQueriesLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    /**
     * Set error state
     */
    setQueriesError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Set context for queries
     */
    setQueryContext: (state, action) => {
      state.context = {
        ...state.context,
        ...action.payload,
      };
    },

    /**
     * Clear all queries and reset state
     */
    clearQueries: (state) => {
      state.queries = [];
      state.queriesByFieldKey = {};
      state.isLoading = false;
      state.error = null;
      state.context = initialState.context;
    },

    /**
     * Update a single query in the list
     */
    updateQuery: (state, action) => {
      const updatedQuery = action.payload;
      const index = state.queries.findIndex((q) => q.id === updatedQuery.id);
      if (index !== -1) {
        state.queries[index] = updatedQuery;

        // Re-index queries by fieldKey
        state.queriesByFieldKey = {};
        state.queries.forEach((query) => {
          if (query.fieldKey) {
            if (!state.queriesByFieldKey[query.fieldKey]) {
              state.queriesByFieldKey[query.fieldKey] = [];
            }
            state.queriesByFieldKey[query.fieldKey].push(query);
          }
        });
      }
    },

    /**
     * Trigger a refetch of queries by incrementing the counter
     * Components watching this value in useEffect dependencies will refetch
     */
    triggerRefetch: (state) => {
      state.refetchTrigger += 1;
    },
  },
});

export const { setQueries, setQueriesLoading, setQueriesError, setQueryContext, clearQueries, updateQuery, triggerRefetch } =
  querySlice.actions;

// Selectors

/**
 * Get all queries
 */
export const selectAllQueries = (state) => state.queries.queries;

/**
 * Get queries indexed by fieldKey
 */
export const selectQueriesByFieldKey = (state) => state.queries.queriesByFieldKey;

/**
 * Get queries for a specific field
 */
export const selectQueriesForField = (fieldKey) => (state) => state.queries.queriesByFieldKey[fieldKey] || [];

/**
 * Check if a field has any queries
 */
export const selectFieldHasQueries = (fieldKey) => (state) =>
  Boolean(state.queries.queriesByFieldKey[fieldKey]?.length > 0);

/**
 * Get open queries (status: OPEN or REOPENED)
 */
export const selectOpenQueries = (state) =>
  state.queries.queries.filter((q) => q.status === 'OPEN' || q.status === 'REOPENED');

/**
 * Check if there are any open queries for the current form
 */
export const selectHasOpenQueries = (state) =>
  state.queries.queries.some((q) => q.status === 'OPEN' || q.status === 'REOPENED');

/**
 * Get open queries for a specific field
 */
export const selectOpenQueriesForField = (fieldKey) => (state) => {
  const fieldQueries = state.queries.queriesByFieldKey[fieldKey] || [];
  return fieldQueries.filter((q) => q.status === 'OPEN' || q.status === 'REOPENED');
};

/**
 * Check if a field has open queries
 */
export const selectFieldHasOpenQueries = (fieldKey) => (state) => {
  const fieldQueries = state.queries.queriesByFieldKey[fieldKey] || [];
  return fieldQueries.some((q) => q.status === 'OPEN' || q.status === 'REOPENED');
};

/**
 * Get loading state
 */
export const selectQueriesLoading = (state) => state.queries.isLoading;

/**
 * Get error state
 */
export const selectQueriesError = (state) => state.queries.error;

/**
 * Get query context
 */
export const selectQueryContext = (state) => state.queries.context;

/**
 * Get query count for a field
 */
export const selectQueryCountForField = (fieldKey) => (state) => state.queries.queriesByFieldKey[fieldKey]?.length || 0;

/**
 * Get open query count for a field
 */
export const selectOpenQueryCountForField = (fieldKey) => (state) => {
  const fieldQueries = state.queries.queriesByFieldKey[fieldKey] || [];
  return fieldQueries.filter((q) => q.status === 'OPEN' || q.status === 'REOPENED').length;
};

/**
 * Get refetch trigger value (used to trigger query refetch)
 */
export const selectRefetchTrigger = (state) => state.queries.refetchTrigger;

export default querySlice.reducer;
